import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { groupChatService } from "@/lib/groupChatService";
import type { SharedGroupChatSessionSummary } from "@/types";
import { useAuth } from "./AuthContext";

interface SharedGroupChatSessionsContextType {
  sharedSessions: SharedGroupChatSessionSummary[];
  isLoading: boolean;
  refreshSharedSessions: () => Promise<void>;
}

const SharedGroupChatSessionsContext = createContext<SharedGroupChatSessionsContextType | null>(null);

/** Minimum time (ms) between API fetches to avoid redundant calls. */
const CACHE_TTL_MS = 30_000; // 30 seconds

export function SharedGroupChatSessionsProvider({
  children,
}: Readonly<{
  children: ReactNode;
}>) {
  const { isAuthenticated } = useAuth();

  const [sharedSessions, setSharedSessions] = useState<SharedGroupChatSessionSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Track the last successful fetch timestamp for cache staleness checks
  const lastFetchedAt = useRef<number>(0);
  // Dedup in-flight requests so concurrent callers share a single promise
  const inflightPromise = useRef<Promise<void> | null>(null);

  const fetchSharedSessions = useCallback(async () => {
    if (!isAuthenticated) return;

    // If data was fetched recently, skip
    if (Date.now() - lastFetchedAt.current < CACHE_TTL_MS) return;

    // If a fetch is already in flight, reuse it instead of firing another
    if (inflightPromise.current) {
      await inflightPromise.current;
      return;
    }

    const promise = (async () => {
      try {
        setIsLoading(true);
        const res = await groupChatService.getSharedWithMe();
        setSharedSessions(res.data);
        lastFetchedAt.current = Date.now();
      } catch (err) {
        console.error("Failed to load shared group chat sessions:", err);
      } finally {
        setIsLoading(false);
        inflightPromise.current = null;
      }
    })();

    inflightPromise.current = promise;
    await promise;
  }, [isAuthenticated]);

  /** Force-refresh regardless of cache TTL (e.g. after sharing/unsharing). */
  const refreshSharedSessions = useCallback(async () => {
    lastFetchedAt.current = 0; // invalidate cache
    await fetchSharedSessions();
  }, [fetchSharedSessions]);

  // Fetch on mount / when auth state changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchSharedSessions();
    } else {
      setSharedSessions([]);
      lastFetchedAt.current = 0;
    }
  }, [isAuthenticated, fetchSharedSessions]);

  const value = useMemo<SharedGroupChatSessionsContextType>(
    () => ({
      sharedSessions,
      isLoading,
      refreshSharedSessions,
    }),
    [sharedSessions, isLoading, refreshSharedSessions],
  );

  return <SharedGroupChatSessionsContext.Provider value={value}>{children}</SharedGroupChatSessionsContext.Provider>;
}

export function useSharedGroupChatSessions(): SharedGroupChatSessionsContextType {
  const context = useContext(SharedGroupChatSessionsContext);
  if (!context) {
    throw new Error("useSharedGroupChatSessions must be used within a SharedGroupChatSessionsProvider");
  }
  return context;
}
